import React, { useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { usePocketBaseRealtime } from '../hooks/usePocketBaseRealtime';
import { dbAdapter } from '../services/dbAdapter';

const { FiClock, FiCheck, FiHash, FiTruck, FiTrash2, FiBell, FiRefreshCw } = FiIcons;

// Icona Cucina SVG professionale
const CucinaIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="30" stroke="#5C715E" strokeWidth="4" fill="#F9F9F9"/>
    <circle cx="32" cy="32" r="14" fill="#E94E3C"/>
    <line x1="16" y1="32" x2="48" y2="32" stroke="#FFFFFF" strokeWidth="2"/>
    <line x1="32" y1="16" x2="32" y2="48" stroke="#FFFFFF" strokeWidth="2"/>
  </svg>
);

const Cucina = () => {
  // Ascolto SSE Unificato di tutte le comande del ristorante corrente
  const { data: comande = [], loading, refresh } = usePocketBaseRealtime('comande');
  
  // 1. Comande attive in preparazione (in cucina)
  const sortedComandeAttive = useMemo(() => {
    return comande
      .filter(c => c.status === 'attiva' && !c.consegnata)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [comande]);

  // 2. Comande completate pronte per la consegna in sala
  const sortedComandePronte = useMemo(() => {
    return comande
      .filter(c => c.status === 'completata' && !c.consegnata)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 15);
  }, [comande]);

  // 3. Comande già consegnate in sala (non ancora pagate, ultime 5)
  const comandeConsegnate = useMemo(() => {
    return comande
      .filter(c => Boolean(c.consegnata) && c.status !== 'pagata')
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5);
  }, [comande]);

  const prevCountRef = useRef(sortedComandeAttive.length);

  // Notifica acustica all'arrivo di una nuova comanda attiva
  useEffect(() => {
    if (sortedComandeAttive.length > prevCountRef.current) {
      try {
        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
        audio.play().catch((err) => console.debug('Audio autoplay blocked:', err));
      } catch (e) {
        console.debug('Audio error:', e);
      }
    }
    prevCountRef.current = sortedComandeAttive.length;
  }, [sortedComandeAttive.length]);

  const completeOrder = async (comandaId) => {
    try {
      await dbAdapter.updateComanda(comandaId, { status: 'completata' });
    } catch (error) {
      console.error('Errore completamento comanda:', error);
    }
  };

  const markAsDelivered = async (comandaId) => {
    try {
      await dbAdapter.updateComanda(comandaId, { consegnata: true });
    } catch (error) {
      console.error('Errore consegna comanda:', error);
    }
  };

  const deleteOrder = async (comandaId) => {
    if (!confirm('Sei sicuro di voler eliminare definitivamente questa comanda?')) return;
    try {
      await dbAdapter.deleteComanda(comandaId);
    } catch (error) {
      console.error('Errore eliminazione comanda:', error);
    }
  };

  const formatTime = (timestamp) => {
    try {
      return format(new Date(timestamp), 'HH:mm', { locale: it });
    } catch {
      return '--:--';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5C715E]"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto px-4 py-8">
      {/* Header Cucina con Pulsante Refresh manuale opzionale */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-6 border-b border-gray-200 gap-4">
        <div className="flex items-center">
          <CucinaIcon className="w-12 h-12 mr-4" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              Produzione Cucina (KDS)
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Ricezione comande in tempo reale dai camerieri e gestione stato piatti
            </p>
          </div>
        </div>
        <button
          onClick={() => refresh()}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
          title="Forza aggiornamento"
        >
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          <span>Aggiorna</span>
        </button>
      </div>

      {/* ── SEZIONE 1: COMANDE ATTIVE IN PREPARAZIONE ── */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-3 h-3 bg-orange-500 rounded-full mr-3 animate-pulse"></div>
          Comande da Preparare ({sortedComandeAttive.length})
          {sortedComandeAttive.length > 0 && (
            <SafeIcon icon={FiBell} className="w-5 h-5 ml-2 text-orange-500 animate-bounce" />
          )}
        </h2>

        {sortedComandeAttive.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
            <SafeIcon icon={FiCheck} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium">Nessuna comanda in attesa di preparazione</p>
            <p className="text-gray-400 text-sm mt-1">Le nuove comande inviate dai camerieri compariranno qui istantaneamente</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {sortedComandeAttive.map((comanda) => (
              <motion.div
                key={comanda.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-l-orange-500 border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                  <div>
                    <h3 className="text-2xl font-black text-gray-900">Tavolo {comanda.tavolo}</h3>
                    <div className="flex items-center text-gray-500 text-xs mt-1">
                      <SafeIcon icon={FiClock} className="w-3.5 h-3.5 mr-1 text-gray-400" />
                      <span>Ricevuta: {formatTime(comanda.timestamp)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="inline-block bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                      In Cucina
                    </span>
                    {comanda.codice_comanda && (
                      <div className="mt-1">
                        <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          #{comanda.codice_comanda}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 mb-6 min-h-[100px]">
                  {Array.isArray(comanda.piatti) && comanda.piatti.map((piatto, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-b-0">
                      <div className="flex-1 pr-2">
                        <span className="font-semibold text-lg text-gray-900">{piatto.nome}</span>
                        {piatto.note && (
                          <p className="text-xs text-orange-600 italic mt-0.5">Nota: {piatto.note}</p>
                        )}
                      </div>
                      <span className="bg-[#5C715E]/15 text-[#5C715E] font-black text-lg px-2.5 py-1 rounded-md">
                        x{piatto.quantita || piatto.quantity || 1}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => completeOrder(comanda.id)}
                  className="w-full bg-[#5C715E] text-white px-4 py-3 rounded-xl font-bold hover:bg-[#4B5C4D] transition-colors flex items-center justify-center shadow-sm"
                >
                  <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" /> Segna come Pronta
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* ── SEZIONE 2: COMANDE PRONTE PER LA CONSEGNA ── */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-3 h-3 bg-emerald-500 rounded-full mr-3"></div>
          Pronte da Portare al Tavolo ({sortedComandePronte.length})
        </h2>

        {sortedComandePronte.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <SafeIcon icon={FiTruck} className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Nessuna comanda completata in attesa del cameriere</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {sortedComandePronte.map((comanda) => (
              <motion.div
                key={comanda.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-l-emerald-500 border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                  <div>
                    <h3 className="text-2xl font-black text-gray-900">Tavolo {comanda.tavolo}</h3>
                    <div className="flex items-center text-gray-500 text-xs mt-1">
                      <SafeIcon icon={FiClock} className="w-3.5 h-3.5 mr-1 text-gray-400" />
                      <span>Completata: {formatTime(comanda.timestamp)}</span>
                    </div>
                  </div>
                  <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                    Pronta
                  </span>
                </div>

                <div className="space-y-2 mb-6 min-h-[80px]">
                  {Array.isArray(comanda.piatti) && comanda.piatti.map((piatto, index) => (
                    <div key={index} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-b-0">
                      <span className="font-medium text-gray-800">{piatto.nome}</span>
                      <span className="text-emerald-700 font-bold ml-2">x{piatto.quantita || piatto.quantity || 1}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => markAsDelivered(comanda.id)}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center shadow-sm"
                >
                  <SafeIcon icon={FiTruck} className="w-5 h-5 mr-2" /> Consegnata in Sala
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* ── SEZIONE 3: ULTIME CONSEGNATE IN SALA ── */}
      {comandeConsegnate.length > 0 && (
        <div className="border-t border-gray-200 pt-8">
          <h2 className="text-xl font-bold text-gray-700 mb-4 flex items-center">
            <div className="w-2.5 h-2.5 bg-blue-500 rounded-full mr-2"></div>
            Ultime Consegnate al Tavolo
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {comandeConsegnate.map((comanda) => (
              <div
                key={comanda.id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm flex items-center justify-between"
              >
                <div>
                  <span className="font-bold text-gray-900">Tavolo {comanda.tavolo}</span>
                  <span className="text-gray-400 text-xs ml-2">({formatTime(comanda.timestamp)})</span>
                  <div className="text-xs text-gray-500 mt-1">
                    {comanda.piatti?.map(p => `${p.nome} (x${p.quantita || 1})`).join(', ')}
                  </div>
                </div>
                <button
                  onClick={() => deleteOrder(comanda.id)}
                  className="text-gray-400 hover:text-red-500 p-1"
                  title="Elimina"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Cucina;